import React, { useState } from 'react';
import { ChevronDown, ChevronUp, User, Clock, FileText, Activity } from 'lucide-react';
import { format } from 'date-fns';

export default function AuditStreamPanel({ logs }) {
  const [expandedLog, setExpandedLog] = useState(null);

  const getActionTypeColor = (actionType) => {
    const colorMap = {
      role_created: 'bg-green-100 text-green-800',
      role_updated: 'bg-blue-100 text-blue-800',
      role_deleted: 'bg-red-100 text-red-800',
      role_assigned: 'bg-purple-100 text-purple-800',
      role_unassigned: 'bg-orange-100 text-orange-800',
      permission_granted: 'bg-green-100 text-green-800',
      permission_revoked: 'bg-red-100 text-red-800',
      permission_updated: 'bg-blue-100 text-blue-800',
      security_setting_updated: 'bg-yellow-100 text-yellow-800',
      rls_policy_check: 'bg-indigo-100 text-indigo-800',
      database_sync: 'bg-teal-100 text-teal-800',
      system_error: 'bg-red-100 text-red-800',
      security_violation: 'bg-red-100 text-red-800'
    };
    return colorMap?.[actionType] || 'bg-gray-100 text-gray-800';
  };

  const formatActionType = (actionType) => {
    return actionType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  if (!logs || logs?.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No audit logs found for the selected filters</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {logs?.map((log) => (
        <div
          key={log?.id}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionTypeColor(log?.actionType)}`}>
                  {formatActionType(log?.actionType)}
                </span>
                <span className="text-xs text-gray-500">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {format(new Date(log?.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                </span>
              </div>
              <p className="text-sm text-gray-900 font-medium mb-2">{log?.actionDescription}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-600">
                {log?.performedByUser && (
                  <span className="flex items-center">
                    <User className="w-3 h-3 mr-1" />
                    {log?.performedByUser?.first_name} {log?.performedByUser?.last_name}
                  </span>
                )}
                {log?.affectedUser && (
                  <span>
                    Affected: {log?.affectedUser?.first_name} {log?.affectedUser?.last_name}
                  </span>
                )}
                {log?.ipAddress && (
                  <span>IP: {log?.ipAddress}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
              className="ml-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              {expandedLog === log?.id ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          {expandedLog === log?.id && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {log?.beforeState && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                      <FileText className="w-3 h-3 mr-1" />
                      Before State
                    </h4>
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(log?.beforeState, null, 2)}
                    </pre>
                  </div>
                )}
                {log?.afterState && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                      <FileText className="w-3 h-3 mr-1" />
                      After State
                    </h4>
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(log?.afterState, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              {log?.changeDetails && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Change Details</h4>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(log?.changeDetails, null, 2)}
                  </pre>
                </div>
              )}
              {log?.userAgent && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-gray-700 mb-1">User Agent</h4>
                  <p className="text-xs text-gray-600">{log?.userAgent}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}